/**
 * The single place that chooses the Pay data source.
 *
 * To go live, the PM implements `PayRepo` over the V2 Payroll API and changes the ONE line
 * in `createDefaultPayRepo()` below — no component or composable changes. Tests inject
 * their own repo with `setPayRepo()` (module singleton) or `providePayRepo()` (component
 * subtree).
 *
 * The mock (and the ~0.5 MB prototype seed behind it) is loaded lazily on the first repo
 * call, so the always-mounted TopBar pill does not pull it into the app-shell bundle.
 */
import { inject, provide, type InjectionKey } from 'vue';
import { DEMO_ENABLED } from '@/config/featureFlags';
import { demoNow } from '@/demo/demoClock';
import type { PayDemoControls, PayRepo } from './repo';

/**
 * PAY_DEMO — the module runs on the Payroll Module prototype's mock data with the demo-only
 * controls ("Reset demo data", the `?demoNow=` clock pin). Tied to the build mode — off in a production build
 * (`DEMO_ENABLED`, `src/config/featureFlags.ts`). Turn off once the V2 repo lands.
 */
export const PAY_DEMO = DEMO_ENABLED;

/** Typed injection key for the repository. */
export const PAY_REPO: InjectionKey<PayRepo> = Symbol('PayRepo');

type LoadedRepo = PayRepo & Partial<PayDemoControls>;

let singleton: PayRepo | null = null;
let loaded: Promise<LoadedRepo> | null = null;

/**
 * A `PayRepo` whose implementation is imported on first use.
 * @param load - Resolves the real repository.
 * @returns A repository that forwards every call once loaded.
 */
export function lazyPayRepo(load: () => Promise<LoadedRepo>): PayRepo & PayDemoControls {
  const get = (): Promise<LoadedRepo> => {
    if (!loaded) loaded = load();
    return loaded;
  };
  return new Proxy({} as PayRepo & PayDemoControls, {
    get(_target, prop) {
      // Never look like a thenable / iterable.
      if (typeof prop !== 'string' || prop === 'then') return undefined;
      if (prop === 'resetDemo') {
        return (): Promise<void> => get().then((r) => r.resetDemo?.());
      }
      return async (...args: unknown[]): Promise<unknown> => {
        const repo = await get();
        const fn = (repo as unknown as Record<string, unknown>)[prop];
        if (typeof fn !== 'function') throw new Error(`PayRepo has no method "${prop}"`);
        return (fn as (...a: unknown[]) => Promise<unknown>).apply(repo, args);
      };
    },
  });
}

/** @returns the default repository for this build. */
export function createDefaultPayRepo(): PayRepo {
  // ← swap to the V2 repo when the backend lands.
  return lazyPayRepo(async () => {
    const { createMockPayRepo } = await import('./mockRepo');
    return createMockPayRepo({ persist: PAY_DEMO, now: () => demoNow({ enabled: PAY_DEMO }) });
  });
}

/** @returns the module-wide repository (created on first use). */
export function getPayRepo(): PayRepo {
  if (!singleton) singleton = createDefaultPayRepo();
  return singleton;
}

/**
 * Replace the module-wide repository (tests). Pass `null` to drop it so the next
 * `getPayRepo()` creates a fresh default.
 * @param repo - The repository, or `null`.
 */
export function setPayRepo(repo: PayRepo | null): void {
  singleton = repo;
  if (!repo) loaded = null;
}

/**
 * Provide a repository to a component subtree.
 * @param repo - The repository.
 */
export function providePayRepo(repo: PayRepo): void {
  provide(PAY_REPO, repo);
}

/** @returns the injected repository, falling back to the module singleton. */
export function usePayRepo(): PayRepo {
  return inject(PAY_REPO, null) || getPayRepo();
}

/**
 * "Reset demo data" for Pay: restores the seeded baseline in the active repo (the
 * `DemoResetButton` clears the storage keys; this re-seeds the in-memory copy). Callers
 * invalidate the `['payroll']` queries afterwards.
 */
export async function resetPayDemo(): Promise<void> {
  const repo = getPayRepo() as Partial<PayDemoControls>;
  await repo.resetDemo?.();
}
